import React, { FC, useEffect, useState, useContext } from "react";
import { Box, IconButton, List, Slide, Stack, Tooltip, Typography } from "@mui/material";
import LibraryAddOutlinedIcon from "@mui/icons-material/LibraryAddOutlined";
import CopyAllOutlinedIcon from "@mui/icons-material/CopyAllOutlined";
import ContentPasteOutlinedIcon from "@mui/icons-material/ContentPasteOutlined";
import HighlightAltOutlinedIcon from "@mui/icons-material/HighlightAltOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import SignalWifiStatusbarNullOutlinedIcon from "@mui/icons-material/SignalWifiStatusbarNullOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import SelectedDevicesBlade from "./SelectedDevicesBlade";
import AvailableDevicesBlade from "./AvailableDevicesBlade";
import theme from "common/theme/theme";
import useDeviceStore from "common/store/DeviceStore";
import useProjectStore from "common/store/ProjectStore";
import LoadingSpinnerBackdrop from "common/components/progress/LoadingSpinner";
import { SharedProjectContext } from "components/project/Project";
import { LanguageContext } from "common/contexts/LanguageContext";
import BladeIconButton from "./BladeIconButton";
import NetworkListItem from "./NetworkListItem";
import useSettingsStore from "common/store/SettingsStore";
import ConfirmationModal from "common/components/dialogs/ConfirmationModal";
import { useAuthContext } from "common/contexts/AuthContext";
import DialogTextField from "common/components/dialogs/DialogTextField";
import { PulsatingBackground } from "common/components/pulsating/PulsatingCircle";
import ArrowCallout from "common/components/ArrowCallout/ArrowCallout";
import NetworkContainerListItem from "./NetworkContainerListItem";
import { getNetworkAntennaName } from "common/utils/networkExtensions";
import { useAuth0 } from '@auth0/auth0-react';

const NetworkLimitPerProject = 5;

interface IProps {
  minBladeHeight: number;
  maxBladeHeight: number;
}

const BladesContainer: FC<IProps> = ({ minBladeHeight, maxBladeHeight }) => {
  const [isNewNetworkOpen, setIsNewNetworkOpen] = useState<boolean>(false);
  const [containerName, setContainerName] = useState<string>("");
  const [showStartCues, setShowStartCues] = useState(false);

  const { fetchDevices, loading } = useDeviceStore();
  const { networkContainers, activeNetwork, addNetworkContainer, setActiveNetworkContainer } = useProjectStore();
  const { settings } = useSettingsStore();
  const { multiSelect, setMultiSelect, copy, paste, deleteSelected, isBusyBackgroundActive, bladesViewAvailable, setBladesViewAvailable } =
    useContext(SharedProjectContext);
  const { getString } = useContext(LanguageContext);
  const { lookupCategory } = useDeviceStore();

    const { isAuthenticatedUser } = useAuthContext();

  useEffect(() => {
    if (settings.apiBaseUrl !== "") {
      fetchDevices(settings.apiBaseUrl);
    }
  }, [settings, fetchDevices]);

  useEffect(() => {
    let lclShowStartCues = !isBusyBackgroundActive && !isNewNetworkOpen && !bladesViewAvailable;

    // If ok to show start cues, verify there is no network containers that already contains a gateway
    if (lclShowStartCues) {
      if (networkContainers.some((container) => container.gatewayDeviceInstance)) {
        lclShowStartCues = false;
      }
    }

    setShowStartCues(lclShowStartCues);
  }, [isNewNetworkOpen, bladesViewAvailable, isBusyBackgroundActive, networkContainers]);

  const handleOnClickViewAvailable = () => {
    setBladesViewAvailable(!bladesViewAvailable);
  };

  const handleNetworkContainerCreation = async () => {
    setIsNewNetworkOpen(false);
    const networkContainerID = await addNetworkContainer(containerName);
    // Reqs: make new network container the active network container
    setActiveNetworkContainer(networkContainerID, true);
  };

  const handleOnClickNewNetwork = () => {
    setContainerName("");
    setIsNewNetworkOpen(true);
  };

  return (
    <>
      {loading ? (
        <LoadingSpinnerBackdrop />
      ) : (
        <>
          <Box display="flex" sx={{ height: "100%", minHeight: `${minBladeHeight}px` }}>
            <Stack
              direction="column"
              justifyContent="left"
              spacing={0}
              sx={{
                backgroundColor: theme.palette.common.white,
                height: "100%",
                width: "250px",
                maxHeight: `${maxBladeHeight}px`,
              }}
            >
              <List sx={{ p: "0" }}>
                <div
                  style={{
                    backgroundColor: theme.accent.light,
                    display: "flex",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <Typography variant="topLevelBlade">{getString("blade_list_networks")}</Typography>
                  <div style={{ flexGrow: 1 }}></div>
                  <Tooltip title={getString("tt_new_network")}>
                    <div>
                      <IconButton
                        disableRipple
                        sx={{
                          margin: "auto",
                          p: "4px",
                          "&:hover": {
                            color: theme.palette.primary.light,
                          },
                        }}
                        disabled={!isAuthenticatedUser}
                        onClick={handleOnClickNewNetwork}
                      >
                        <Box
                          sx={{
                            position: "relative",
                            width: 24,
                            height: 24,
                          }}
                        >
                          <SignalWifiStatusbarNullOutlinedIcon
                            sx={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                            }}
                          />
                          <AddOutlinedIcon
                            sx={{
                              position: "absolute",
                              top: 6,
                              left: 6,
                              width: 12,
                              height: 12,
                            }}
                          />
                        </Box>
                      </IconButton>
                    </div>
                  </Tooltip>
                </div>
                {networkContainers.flatMap((container, index) => [
                  <NetworkContainerListItem key={`container-${index}`} container={container}/>,
                  ...container.networks.map((network, index2) => (
                    <NetworkListItem key={`net-${index}-${index2}`} network={network} />
                  ))
                ])}
              </List>
              <div
                style={{ marginTop: 0, paddingTop: "6px", paddingBottom: "6px", backgroundColor: theme.accent.light }}
              >
                <Typography variant="topLevelBlade">
                  {getString("blade_assets_in")} {activeNetwork && getNetworkAntennaName(activeNetwork, lookupCategory, getString('mnu_unnamed_nwk'))}
                </Typography>
              </div>
              <Box sx={{ overflowY: "auto", boxSizing: "border-box", mt: 0 }}>
                <SelectedDevicesBlade />
              </Box>
            </Stack>
            <Stack
              direction="column"
              justifyContent="left"
              spacing={0}
              sx={{ backgroundColor: theme.accent.light, width: "40px" }}
            >
              {showStartCues ? (
                <ArrowCallout
                  open={true}
                  content={
                    <div>
                      Click <LibraryAddOutlinedIcon /> and drag a gateway to your map to get started.
                    </div>
                  }
                >
                  <BladeIconButton
                    disableRipple
                    onClick={handleOnClickViewAvailable}
                    sx={{
                      color: bladesViewAvailable === true ? theme.palette.action.hover : "default",
                    }}
                  >
                    <PulsatingBackground backgroundColor={theme.palette.primary.light}>
                      <LibraryAddOutlinedIcon />
                    </PulsatingBackground>
                  </BladeIconButton>
                </ArrowCallout>
              ) : (
                <Tooltip title={getString(bladesViewAvailable === true ? "tip_collapse_add_items" : "tip_expand_add_items")}>
                  <BladeIconButton
                    disableRipple
                    onClick={handleOnClickViewAvailable}
                    sx={{
                      color: bladesViewAvailable === true ? theme.palette.action.hover : "default",
                    }}
                  >
                    <LibraryAddOutlinedIcon />
                  </BladeIconButton>
                </Tooltip>
              )}
              <Tooltip title={getString("tip_copy_devices")}>
                <BladeIconButton disableRipple onClick={copy}>
                  <CopyAllOutlinedIcon />
                </BladeIconButton>
              </Tooltip>
              <Tooltip title={getString("tip_paste_devices")}>
                <BladeIconButton disableRipple onClick={paste}>
                  <ContentPasteOutlinedIcon />
                </BladeIconButton>
              </Tooltip>
              <Tooltip title={getString("tip_select_devices")}>
                <BladeIconButton disableRipple onClick={() => setMultiSelect(!multiSelect)}>
                  <HighlightAltOutlinedIcon />
                </BladeIconButton>
              </Tooltip>
              <Tooltip title={getString("tip_delete_devices")}>
                <BladeIconButton disableRipple onClick={deleteSelected}>
                  <DeleteOutlineOutlinedIcon />
                </BladeIconButton>
              </Tooltip>
            </Stack>
            <Box position="relative" bgcolor="yellow">
              <Box
                width="300px"
                height="100%"
                position="absolute"
                top={0}
                left={0}
                right={0}
                zIndex={1}
                sx={{
                  overflow: "hidden",
                  pointerEvents: bladesViewAvailable ? "auto" : "none",
                }}
                id="device-blade-overlay"
              >
                <Slide
                  direction="right"
                  in={bladesViewAvailable}
                  // mountOnEnter
                  // unmountOnExit
                  timeout={300}
                >
                  <Stack
                    direction="column"
                    justifyContent="left"
                    spacing={0}
                    visibility={bladesViewAvailable === true ? "visible" : "hidden"}
                    sx={{ backgroundColor: theme.accent.light, height: "100%", maxwidth: "250px" }}
                  >
                    <div style={{ marginTop: 0 }}>
                      <Typography
                        sx={{
                          backgroundColor: theme.accent.light,
                          pt: "8px",
                          pb: "8px",
                          color: theme.palette.common.black,
                          fontSize: "12px",
                          fontWeight: "bold",
                        }}
                      >
                        {getString("blade_add_items")}
                      </Typography>
                    </div>
                    <Box
                      sx={{ pr: "4px", bgcolor: theme.palette.grey[50], overflowY: "auto", boxSizing: "border-box" }}
                    >
                      <AvailableDevicesBlade />
                    </Box>
                  </Stack>
                </Slide>
              </Box>
            </Box>
          </Box>

          {networkContainers.length < NetworkLimitPerProject ? (
            <ConfirmationModal
              isOpen={isNewNetworkOpen}
              handleClose={() => {
                setIsNewNetworkOpen(false);
              }}
              handleConfirm={handleNetworkContainerCreation}
              title={getString("dlg_new_nwk_title")}
              maxWidth="sm"
              confirmBtnLabel={getString("cmn_create")}
              hideDivider={true}
            >
              <DialogTextField
                sx={{ pt: "24px" }}
                variant="filled"
                size="small"
                label={getString("dlg_new_nwk_name_lbl")}
                value={containerName}
                onChange={(e) => setContainerName(e.target.value)}
              />
            </ConfirmationModal>
          ) : (
            <ConfirmationModal
              isOpen={isNewNetworkOpen}
              handleClose={() => {}}
              hideCancel
              handleConfirm={() => {
                setIsNewNetworkOpen(false);
              }}
              title={getString("dlg_new_nwk_title")}
              maxWidth="sm"
              confirmBtnLabel={getString("cmn_ok")}
              hideDivider={true}
            >
              <Typography paragraph sx={{ p: "24px" }}>
                {getString("blade_network_limit")}
              </Typography>
            </ConfirmationModal>
          )}
        </>
      )}
    </>
  );
};

export default BladesContainer;
